import { getDefinedUrlParams } from "@libs/utils";
import { ServiceBase } from "services/base-service";
import type { ProjectResponse, IProjectId } from "features/projects/types";
import type {
  WorkspacesResponse,
  IFilesPageableRequest,
  IFilesResponse,
  IEditorFetchParams,
  ITranslationItemsResponse,
  ISimilarTranslationItemsFetchParams,
  ISimilarTranslationItems,
} from "features/workspaces/types";
import { escapeSearchQuery } from "features/languages/utils/rules";
import type { IPageableList } from "types";

class WorkspacesService extends ServiceBase {
  constructor() {
    super();

    this.baseUrl = `${this.baseUrl}/Workspaces`;

    this.getFiles = this.getFiles.bind(this);
    this.getTranslationItems = this.getTranslationItems.bind(this);
    this.getApprovedTranslationItemsCount = this.getApprovedTranslationItemsCount.bind(this);
    this.deleteWorkspace = this.deleteWorkspace.bind(this);
    this.deleteProject = this.deleteProject.bind(this);
  }

  async getWorkspaces(abortController?: AbortController): Promise<WorkspacesResponse> {
    const response = await this.sendGet("", abortController);

    return response as WorkspacesResponse;
  }

  async deleteWorkspace(id: number, abortController?: AbortController): Promise<string> {
    const url = `/${id}`;
    const response = await this.sendDelete(url, abortController);

    return response;
  }

  async getFiles(
    request: IFilesPageableRequest,
    abortController?: AbortController,
  ): Promise<IPageableList<IFilesResponse>> {
    const params = {
      projectLanguageId: request.projectLanguageId,
      page: request.page,
      pageSize: request.pageSize,
      orderBy: request.orderBy,
      descendingOrder: request.descendingOrder.toString(),
      search: request.search,
    };

    const url = `/Files?${getDefinedUrlParams(params)}`;
    const response = await this.sendGet(url, abortController);

    return response;
  }

  async getProject({ id }: { id: IProjectId }, abortController?: AbortController): Promise<ProjectResponse> {
    const url = `/Project/${id}`;
    const response = await this.sendGet(url, abortController);

    return response;
  }

  async deleteProject(id: number, abortController?: AbortController): Promise<string> {
    const url = `/Project/${id}`;
    const response = await this.sendDelete(url, abortController);

    return response;
  }

  async getTranslationItems(
    request: IEditorFetchParams,
    abortController?: AbortController,
  ): Promise<ITranslationItemsResponse> {
    const params = {
      fileId: request.fileId,
      page: request.page,
      pageSize: request.pageSize,
      sort: request.itemsSort,
      orderBy: request.orderBy,
      descendingOrder: request.descendingOrder,
      search: request.search,
      searchIdInFile: request.searchIdInFile,
      searchOriginal: escapeSearchQuery(request.searchOriginal),
      searchTranslation: escapeSearchQuery(request.searchTranslation),
      searchGroup: request.searchGroup,
      showInvalidOnly: request.showInvalidOnly,
      searchExact: request.searchExact,
      searchGroupFullMatch: request.searchGroupFullMatch ?? false,
      searchWholeProject: request.searchWholeProject ?? undefined,
    };

    const url = `/TranslationItems?${getDefinedUrlParams(params)}`;
    const response = await this.sendGet(url, abortController);

    return response;
  }

  async getApprovedTranslationItemsCount(
    request: IEditorFetchParams,
    abortController?: AbortController,
  ): Promise<number> {
    const params = {
      fileId: request.fileId,
      searchIdInFile: request.searchIdInFile,
      searchOriginal: escapeSearchQuery(request.searchOriginal),
      searchTranslation: escapeSearchQuery(request.searchTranslation),
      searchGroup: request.searchGroup,
      showInvalidOnly: request.showInvalidOnly,
      searchGroupFullMatch: request.searchGroupFullMatch ?? false,
    };

    const url = `/ApprovedTranslationItemsCount?${getDefinedUrlParams(params)}`;
    const response = await this.sendGet(url, abortController);

    return response;
  }

  async checkProject(projectId: number, abortController?: AbortController): Promise<void> {
    const url = `/Project/Validate`;
    const response = await this.sendPatch(
      url,
      {
        projectId,
      },
      abortController,
    );

    console.log(response);
  }

  async getSimilarTranslationItems(
    request: ISimilarTranslationItemsFetchParams,
    abortController?: AbortController,
  ): Promise<ISimilarTranslationItems> {
    const params = {
      fileId: request.fileId,
      searchOriginal: escapeSearchQuery(request.searchOriginal),
    };

    const url = `/SimilarTranslationItems?${getDefinedUrlParams(params)}`;
    const response = await this.sendGet(url, abortController);

    return response;
  }
}

const workspacesService = new WorkspacesService();

export { workspacesService };
